import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Shield,
  Clock,
  FolderOpen,
  AlertTriangle,
  Search,
  Loader2,
  ShieldCheck,
  Info,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { HealthScoreRing } from '@/components/shared/health-score-ring';
import { StatsCard } from '@/components/dashboard/stats-card';
import { FindingSummaryChart } from '@/components/dashboard/finding-summary-chart';
import { FindingTrendsChart } from '@/components/dashboard/finding-trends-chart';
import { AGENT_MAP, COLOR_MAP } from '@/lib/agent-meta';
import { cn } from '@/lib/utils';
import type { FindingCounts } from '@nirvik/shared';

type RunStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface DashboardRun {
  runId: string;
  projectId: string;
  portfolioId: string | null;
  projectName: string;
  targetUrl: string;
  agentType: string;
  status: RunStatus;
  progress: number;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  duration: number | null;
  findingCount: number;
}

export interface TrendPoint {
  day: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface DashboardSummaryData {
  healthScore: number;
  totalProjects: number;
  totalRuns: number;
  findingCounts: FindingCounts;
  recentRuns: DashboardRun[];
  findingTrends: TrendPoint[];
}

interface Props {
  orgId: string;
  /** href that "View all" / stats cards link to (org or portfolio scope). */
  drillHref: string;
  /** Build the per-run detail link. orgId from outer scope, portfolioId
   *  from the run row. Returns null to render the row as non-clickable. */
  runHref: (run: DashboardRun) => string | null;
  data: DashboardSummaryData;
  /** Optional slot rendered between the trend chart and Recent Agent Runs.
   *  The org dashboard uses this to inject the portfolio cards section;
   *  the portfolio dashboard leaves it unset. */
  beforeRecentRuns?: React.ReactNode;
}

const statusColors: Record<RunStatus, { bg: string; text: string; dot: string }> = {
  completed: { bg: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', dot: 'bg-green-500' },
  running: { bg: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', dot: 'bg-blue-500' },
  failed: { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', dot: 'bg-red-500' },
  queued: { bg: 'bg-gray-50 dark:bg-muted', text: 'text-muted-foreground', dot: 'bg-gray-400' },
  cancelled: { bg: 'bg-gray-50 dark:bg-muted', text: 'text-muted-foreground', dot: 'bg-gray-400' },
};

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Shared dashboard widgets used by both the org dashboard and the per-
 * portfolio dashboard. The two callers differ only in (a) the data
 * source (org vs portfolio summary endpoint) and (b) the URLs the cards
 * link to — both are passed via props.
 */
export function DashboardSummary({ drillHref, runHref, data, beforeRecentRuns }: Props) {
  return (
    <>
      {/* Top row: Health Score + Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Brand-aligned score card: turmeric primary → saffron gradient,
            with the Indian-flag accents. Info pill links to the scoring
            guide so users can learn how the number is calculated. */}
        <Card className="lg:col-span-3 p-8 flex flex-col items-center justify-center bg-gradient-to-br from-primary/10 via-saffron/10 to-chakra-navy/10 dark:from-primary/15 dark:via-saffron/15 dark:to-chakra-navy/20 border-primary/20 dark:border-primary/30 ring-1 ring-primary/15 dark:ring-primary/20 shadow-lg shadow-primary/5 dark:shadow-primary/10">
          <div className="flex items-center gap-1.5 mb-5">
            <h2 className="text-base font-bold text-foreground uppercase tracking-widest">Security Score</h2>
            <Link
              to="/my/learn/scoring"
              title="How is this calculated? — Open the scoring guide"
              aria-label="How is the security score calculated?"
              className="text-muted-foreground hover:text-primary-strong transition-colors"
            >
              <Info className="h-4 w-4" />
            </Link>
          </div>
          <HealthScoreRing score={data.healthScore} size={150} strokeWidth={10} />
        </Card>

        <div className="lg:col-span-9 grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatsCard
            icon={FolderOpen}
            label="Projects"
            value={data.totalProjects}
            href={drillHref}
            iconColor="text-blue-600 dark:text-blue-400"
            iconBg="bg-blue-100 dark:bg-blue-900/50"
            accent="blue"
          />
          <StatsCard
            icon={Shield}
            label="Agent Runs"
            value={data.totalRuns}
            href={drillHref}
            iconColor="text-indigo-600 dark:text-indigo-400"
            iconBg="bg-indigo-100 dark:bg-indigo-900/50"
            accent="indigo"
          />
          <StatsCard
            icon={AlertTriangle}
            label="Open Findings"
            value={data.findingCounts.total}
            href={drillHref}
            iconColor="text-orange-600 dark:text-orange-400"
            iconBg="bg-orange-100 dark:bg-orange-900/50"
            accent="orange"
          />
          <StatsCard
            icon={Search}
            label="Critical Issues"
            value={data.findingCounts.critical}
            href={drillHref}
            trend={
              data.findingCounts.critical > 0
                ? { direction: 'up', label: `${data.findingCounts.critical}` }
                : undefined
            }
            iconColor="text-red-600 dark:text-red-400"
            iconBg="bg-red-100 dark:bg-red-900/50"
            accent="red"
          />
        </div>
      </div>

      {/* Middle row: Findings by Severity + Findings Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6">
          <h2 className="text-base font-bold text-foreground uppercase tracking-wide mb-4">Findings by Severity</h2>
          <FindingSummaryChart counts={data.findingCounts} />
        </Card>

        <Card className="p-6">
          <h2 className="text-base font-bold text-foreground uppercase tracking-wide mb-1">Findings Trend</h2>
          <p className="text-xs text-muted-foreground mb-4">New findings opened, last 30 days</p>
          <FindingTrendsChart data={data.findingTrends} />
        </Card>
      </div>

      {beforeRecentRuns}

      {/* Recent Agent Runs */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-foreground uppercase tracking-wide">Recent Agent Runs</h2>
          <Link
            to={drillHref}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1"
          >
            View all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        {data.recentRuns.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">No agent runs yet. Open a project to launch one.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {data.recentRuns.map((run) => {
              const colors = statusColors[run.status] || statusColors.queued;
              const meta = AGENT_MAP[run.agentType];
              const AgentIcon = meta?.icon ?? ShieldCheck;
              const agentTone = COLOR_MAP[meta?.color ?? 'blue'];
              const agentName = meta?.name ?? run.agentType;
              const href = runHref(run);
              const cardEl = (
                <Card className="p-4 hover:bg-gray-50 dark:hover:bg-muted transition-colors cursor-pointer">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center shrink-0', agentTone.bg)}>
                        {run.status === 'running' ? (
                          <Loader2 className={cn('h-5 w-5 animate-spin', agentTone.text)} />
                        ) : (
                          <AgentIcon className={cn('h-5 w-5', agentTone.text)} />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {agentName} <span className="text-muted-foreground font-normal">· {run.projectName}</span>
                        </p>
                        <p className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="font-mono">{run.agentType}</span>
                          <span>|</span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatRelativeTime(run.createdAt)}
                          </span>
                          {run.duration !== null && (
                            <>
                              <span className="hidden sm:inline">|</span>
                              <span className="hidden sm:inline">{formatDuration(run.duration)}</span>
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {run.findingCount > 0 && (
                        <span className="text-xs text-muted-foreground hidden sm:block">{run.findingCount} findings</span>
                      )}
                      <span
                        className={cn(
                          'text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1.5',
                          colors.bg,
                          colors.text,
                        )}
                      >
                        <span
                          className={cn(
                            'h-1.5 w-1.5 rounded-full',
                            colors.dot,
                            run.status === 'running' && 'animate-pulse',
                          )}
                        />
                        <span className="capitalize">{run.status}</span>
                      </span>
                    </div>
                  </div>
                </Card>
              );
              return href ? (
                <Link key={run.runId} to={href} className="block">{cardEl}</Link>
              ) : (
                <div key={run.runId}>{cardEl}</div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
